import { createTheme, alpha } from '@mui/material/styles';
import DraggableDialogPaper from './components/shared/DraggableDialogPaper';

// Design tokens based on the new AJ logo
const designTokens = {
  colors: {
    primary: '#1a365d',      // Deep navy from logo
    primaryLight: '#2d4a6f',
    primaryDark: '#0f2340',
    accent: '#3182ce',       // Brighter blue for CTAs
    accentLight: '#4299e1',

    // Status colors
    planned: '#f6ad55',      // Orange
    inProgress: '#4299e1',   // Blue
    completed: '#48bb78',    // Green
    pickedUp: '#9f7aea',     // Purple
    cancelled: '#fc8181',    // Red

    // Status backgrounds
    plannedBg: '#fef3e2',
    inProgressBg: '#ebf4ff',
    completedBg: '#e6ffed',
    pickedUpBg: '#f3e8ff',
    cancelledBg: '#fee2e2',

    // Neutrals
    bg: '#f8fafc',
    card: '#ffffff',
    border: '#e2e8f0',
    textPrimary: '#1a202c',
    textSecondary: '#64748b',
    textMuted: '#94a3b8',
  },
  typography: {
    fontFamily: "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  borderRadius: {
    sm: '8px',
    md: '10px',
    lg: '12px',
    xl: '16px',
  },
  shadows: {
    card: '0 1px 3px rgba(0,0,0,0.05)',
    button: '0 4px 12px rgba(49, 130, 206, 0.3)',
    modal: '0 20px 60px rgba(0,0,0,0.3)',
  }
};

const theme = createTheme({
  palette: {
    primary: {
      main: designTokens.colors.primary,
      light: designTokens.colors.primaryLight,
      dark: designTokens.colors.primaryDark,
    },
    secondary: {
      main: designTokens.colors.accent,
      light: designTokens.colors.accentLight,
    },
    background: {
      default: designTokens.colors.bg,
      paper: designTokens.colors.card,
    },
    text: {
      primary: designTokens.colors.textPrimary,
      secondary: designTokens.colors.textSecondary,
    },
    divider: designTokens.colors.border,
    // Custom status colors
    status: {
      planned: designTokens.colors.planned,
      inProgress: designTokens.colors.inProgress,
      completed: designTokens.colors.completed,
      pickedUp: designTokens.colors.pickedUp,
      cancelled: designTokens.colors.cancelled,
      plannedBg: designTokens.colors.plannedBg,
      inProgressBg: designTokens.colors.inProgressBg,
      completedBg: designTokens.colors.completedBg,
      pickedUpBg: designTokens.colors.pickedUpBg,
      cancelledBg: designTokens.colors.cancelledBg,
    },
    error: {
      main: designTokens.colors.cancelled,
    },
    success: {
      main: designTokens.colors.completed,
    },
    warning: {
      main: designTokens.colors.planned,
    },
    info: {
      main: designTokens.colors.inProgress,
    },
  },
  typography: {
    fontFamily: designTokens.typography.fontFamily,
    h1: {
      fontWeight: 700,
      fontSize: '2rem',
      letterSpacing: '-0.5px',
    },
    h2: {
      fontWeight: 700,
      fontSize: '1.75rem',
      letterSpacing: '-0.5px',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.5rem',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.25rem',
    },
    h5: {
      fontWeight: 600,
      fontSize: '1rem',
    },
    h6: {
      fontWeight: 600,
      fontSize: '0.875rem',
    },
    subtitle1: {
      fontSize: '0.9375rem',
      color: designTokens.colors.textSecondary,
    },
    subtitle2: {
      fontSize: '0.875rem',
      color: designTokens.colors.textSecondary,
    },
    body1: {
      fontSize: '0.9375rem',
    },
    body2: {
      fontSize: '0.8125rem',
      color: designTokens.colors.textSecondary,
    },
    button: {
      fontWeight: 500,
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 10,
  },
  shadows: [
    'none',
    designTokens.shadows.card,
    '0 2px 4px rgba(0,0,0,0.08)',
    '0 4px 6px rgba(0,0,0,0.1)',
    '0 6px 12px rgba(0,0,0,0.1)',
    '0 8px 16px rgba(0,0,0,0.1)',
    '0 12px 24px rgba(0,0,0,0.15)',
    '0 16px 32px rgba(0,0,0,0.15)',
    designTokens.shadows.modal,
    ...Array(16).fill('0 20px 40px rgba(0,0,0,0.2)'),
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: designTokens.colors.bg,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: designTokens.borderRadius.md,
          padding: '10px 20px',
          fontWeight: 500,
          fontSize: '0.875rem',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: designTokens.shadows.button,
          },
        },
        containedPrimary: {
          backgroundColor: designTokens.colors.accent,
          '&:hover': {
            backgroundColor: designTokens.colors.accentLight,
          },
        },
        outlined: {
          borderColor: designTokens.colors.border,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: designTokens.borderRadius.lg,
          boxShadow: designTokens.shadows.card,
          border: `1px solid ${designTokens.colors.border}`,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: designTokens.borderRadius.lg,
          boxShadow: designTokens.shadows.card,
          border: `1px solid ${designTokens.colors.border}`,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: designTokens.borderRadius.md,
          },
        },
      },
    },
    MuiDialog: {
      // Every dialog can be dragged aside by its title on desktop (full-screen ones excluded
      // inside the component). One default here instead of 160 opt-ins, so new dialogs match.
      defaultProps: {
        PaperComponent: DraggableDialogPaper,
      },
      styleOverrides: {
        paper: {
          borderRadius: designTokens.borderRadius.xl,
          boxShadow: designTokens.shadows.modal,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: designTokens.colors.primary,
          color: 'white',
          border: 'none',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: designTokens.borderRadius.sm,
          marginBottom: '4px',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(255, 255, 255, 0.15)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
            },
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: designTokens.colors.bg,
          '& .MuiTableCell-root': {
            fontWeight: 600,
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            color: designTokens.colors.textSecondary,
            borderBottom: `1px solid ${designTokens.colors.border}`,
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: designTokens.colors.bg,
          },
          cursor: 'pointer',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${designTokens.colors.border}`,
          padding: '16px 24px',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '20px',
          fontWeight: 500,
          fontSize: '0.8125rem',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: designTokens.colors.primary,
          boxShadow: 'none',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          color: designTokens.colors.textPrimary,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: designTokens.borderRadius.md,
        },
      },
    },
  },
  // Custom design tokens for easy access
  customTokens: designTokens,
});

export { designTokens };
export default theme;
